import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { DemoHint } from "@/components/demo/DemoHint";
import { useAuth } from "@/contexts/AuthContext";
import { useCompanyAccount } from "@/hooks/useCompanyAccount";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CompanyOverviewTab } from "@/components/company-settings/CompanyOverviewTab";
import { CompanyTeamTab } from "@/components/company-settings/CompanyTeamTab";
import { CompanyCreditsTab } from "@/components/company-settings/CompanyCreditsTab";
import { CompanyUsageTab } from "@/components/company-settings/CompanyUsageTab";
import { CompanyBillingTab } from "@/components/company-settings/CompanyBillingTab";
import { CompanyCreditProfileTab } from "@/components/company-settings/CompanyCreditProfileTab";
import { BrokerCreditAppTemplateTab } from "@/components/company-settings/BrokerCreditAppTemplateTab";
import { CompanyVerificationTab } from "@/components/company-settings/CompanyVerificationTab";
import { CompanyTeamActivityTab } from "@/components/company-settings/CompanyTeamActivityTab";
import { InviteUserDialog } from "@/components/company-settings/InviteUserDialog";
import { Building2, AlertCircle, ArrowLeft } from "lucide-react";
import { Card } from "@/components/ui/card";

export default function CompanySettingsPage() {
  const { user, profile } = useAuth();
  const {
    company,
    members,
    myRole,
    isAdmin,
    walletBalance,
    transactions,
    subscription,
    pendingInvites,
    companyId,
    loading,
    refresh,
  } = useCompanyAccount();

  const [inviteOpen, setInviteOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "overview";
  const setActiveTab = (next: string) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set("tab", next);
      return p;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
      </div>
    );
  }

  if (!company) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="p-8 max-w-md text-center">
          <AlertCircle className="h-10 w-10 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-foreground mb-2">No Company Account</h2>
          <p className="text-sm text-muted-foreground">
            You are not associated with a company yet. Contact your administrator to get added to a company account.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6">
      {activeTab === "broker-credit-app" && (
        <Link
          to="/lr-direct/broker"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Dashboard
        </Link>
      )}
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
          <Building2 className="h-5 w-5 text-primary" />
        </div>
        <div>
          <DemoHint hintKey="page.companySettings" side="bottom" showIndicator>
            <h1 className="text-xl font-bold text-foreground">Company Settings</h1>
          </DemoHint>
          <p className="text-sm text-muted-foreground">{company.name}</p>
        </div>
      </div>

      {/* Tabs */}
      {(() => {
        const showCreditProfile = profile?.user_type === "shipper";
        const showBrokerCreditAppTemplate =
          (profile?.user_type === "broker" || profile?.user_type === "broker_carrier") &&
          isAdmin &&
          !!companyId &&
          !profile?.is_demo_user;
        return (
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full justify-start overflow-x-auto flex-nowrap h-auto p-1 [&>button]:flex-shrink-0 [&>button]:whitespace-nowrap">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="team">Team</TabsTrigger>
          {isAdmin && <TabsTrigger value="team-activity">Team Activity</TabsTrigger>}
          <TabsTrigger value="verification">Verification</TabsTrigger>
          {showCreditProfile && <TabsTrigger value="credit-profile">Credit Profile</TabsTrigger>}
          {showBrokerCreditAppTemplate && (
            <TabsTrigger value="credit-app-template">Credit App Template</TabsTrigger>
          )}
          {isAdmin && <TabsTrigger value="billing">Billing</TabsTrigger>}
          <TabsTrigger value="credits">Credits</TabsTrigger>
          <TabsTrigger value="usage">Usage</TabsTrigger>
        </TabsList>


        {companyId && (
          <TabsContent value="verification">
            <CompanyVerificationTab
              companyId={companyId}
              role={profile?.user_type || company.company_type}
              isAdmin={isAdmin}
              documentsVerifiedAt={company.documents_verified_at}
            />
          </TabsContent>
        )}

        {showBrokerCreditAppTemplate && companyId && (
          <TabsContent value="credit-app-template">
            <BrokerCreditAppTemplateTab companyId={companyId} />
          </TabsContent>
        )}

        <TabsContent value="overview">
          <CompanyOverviewTab company={company} />
        </TabsContent>

        <TabsContent value="team">
          <CompanyTeamTab
            members={members}
            isAdmin={isAdmin}
            currentUserId={user?.id || ""}
            companyId={companyId || ""}
            companyName={company.name}
            companyType={company.company_type}
            pendingInvites={pendingInvites}
            seatsPurchased={subscription?.seats_purchased || 1}
            seatsUsed={subscription?.seats_used || 1}
            onInviteClick={() => setInviteOpen(true)}
            onUpgradeClick={() => setActiveTab("billing")}
            onRefresh={refresh}
          />
        </TabsContent>

        {isAdmin && companyId && (
          <TabsContent value="team-activity">
            <CompanyTeamActivityTab companyId={companyId} companyName={company.name} companyType={company.company_type} />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="billing">
            <CompanyBillingTab company={company} subscription={subscription} />
          </TabsContent>
        )}

        {showCreditProfile && companyId && (
          <TabsContent value="credit-profile">
            <CompanyCreditProfileTab companyId={companyId} canEdit={isAdmin} />
          </TabsContent>
        )}

        <TabsContent value="credits">
          <CompanyCreditsTab balance={walletBalance} transactions={transactions} />
        </TabsContent>

        <TabsContent value="usage">
          <CompanyUsageTab />
        </TabsContent>
      </Tabs>
        );
      })()}

      {/* Invite dialog */}
      {companyId && (
        <InviteUserDialog
          open={inviteOpen}
          onOpenChange={setInviteOpen}
          companyId={companyId}
          companyName={company.name}
          companyType={company.company_type}
          onInviteSent={refresh}
        />
      )}
    </div>
  );
}
